import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { createTheme, ThemeProvider, Theme, Direction } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

interface LanguageContextType {
  language: string;
  isRTL: boolean;
  changeLanguage: (lang: string) => void;
  theme: Theme;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Create RTL cache
const cacheRtl = createCache({
  key: 'muirtl',
});

// Create LTR cache
const cacheLtr = createCache({
  key: 'muiltr',
});

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || 'en');
  const [isRTL, setIsRTL] = useState(language === 'ar');

  // Create theme based on direction
  const theme = React.useMemo(() => {
    const direction: Direction = isRTL ? 'rtl' : 'ltr';
    
    return createTheme({
      direction,
      palette: {
        primary: {
          main: '#1976d2',
        },
        secondary: {
          main: '#dc004e',
        },
      },
      typography: {
        fontFamily: isRTL ? 
          '"Cairo", "Roboto", "Helvetica", "Arial", sans-serif' : 
          '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
          fontSize: '2.5rem',
          fontWeight: isRTL ? 600 : 300,
        },
        h2: {
          fontSize: '2rem',
          fontWeight: isRTL ? 600 : 300,
        },
        h3: {
          fontSize: '1.75rem',
          fontWeight: isRTL ? 600 : 400,
        },
        h4: {
          fontSize: '1.5rem',
          fontWeight: isRTL ? 600 : 400,
        },
        h5: {
          fontSize: '1.25rem',
          fontWeight: isRTL ? 600 : 400,
        },
        h6: {
          fontSize: '1rem',
          fontWeight: isRTL ? 600 : 500,
        },
        body1: {
          fontSize: '1rem',
        },
        body2: {
          fontSize: '0.875rem',
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              fontFamily: isRTL ? 
                '"Cairo", "Roboto", "Helvetica", "Arial", sans-serif' : 
                '"Roboto", "Helvetica", "Arial", sans-serif',
              direction,
            },
            html: {
              direction,
            },
          },
        },
        MuiTextField: {
          defaultProps: {
            variant: 'outlined',
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 8,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
    });
  }, [isRTL]);

  const changeLanguage = async (lang: string) => {
    try {
      await i18n.changeLanguage(lang);
      setLanguage(lang);
      setIsRTL(lang === 'ar');
      
      // Update document direction
      document.dir = lang === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
      
      // Update document language
      document.documentElement.lang = lang;
      
      // Store in localStorage
      localStorage.setItem('language', lang);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  useEffect(() => {
    // Set initial direction
    document.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [isRTL, language]);

  const contextValue: LanguageContextType = {
    language,
    isRTL,
    changeLanguage,
    theme,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      <CacheProvider value={isRTL ? cacheRtl : cacheLtr}>
        <ThemeProvider theme={theme}>
          {children}
        </ThemeProvider>
      </CacheProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;
